import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  getCompanyAddressDisplayFields,
  translateCompanyCountry,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import CompanyEditFormModal from "./CompanyEditForm";
import AppButton from "../common/AppButton";
import { EditOutlined } from "@mui/icons-material";
import { useGetCompanyDetails } from "../../hooks/api/useCompany";
import NoCompanyDetailFound from "./NoCompanyDetailFound";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useOrganizationLabels } from "../../hooks/useOrganizationLabels";
import {
  formatVatCalculationModeDisplay,
  formatVatPercentageDisplay
} from "./utils/companyVatDisplay";
import { VatCalculationMode } from "./constants/companyVatConstants";
const CompanyView: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const org = useOrganizationLabels();
  const [openEditModal, setOpenEditModal] = useState(false);

  const handleCloseModal = () => setOpenEditModal(false);

  const companyData = useSelector(
    (state: RootState) => state.profile.companyData
  );

  const { mutate: getCompanyDetails, isPending } = useGetCompanyDetails();

  useEffect(() => {
    getCompanyDetails();
  }, [getCompanyDetails]);

  if (isPending) {
    return <CommonLoader loadingMessage={org.fetchingDetail} />;
  }

  if (!companyData) return <NoCompanyDetailFound />;

  const renderField = (label: string, value?: string) => {
    const displayValue =
      label === "country" && value ? translateCompanyCountry(value, t) : value;

    return (
      <Box sx={{ flex: "1 1 45%", mb: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {t(label, label.replace(/-/g, " "))}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: displayValue ? "text.primary" : "text.disabled",
            fontWeight: 500
          }}
        >
          {displayValue || "N/A"}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Card
        data-testid="company-view-card"
        sx={{
          borderRadius: 3,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          p: { xs: 2, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
        }}
      >
        <CardContent sx={{ pt: 2, "&:first-of-type": { pt: 1 } }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              mb: 3,
              mt: 1
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {org.informationTitle}
            </Typography>

            <AppButton
              variant="primary"
              onClick={() => setOpenEditModal(true)}
              startIcon={<EditOutlined />}
              sx={{ mt: { xs: 2, sm: 0 } }}
              data-testid="company-edit-button"
            >
              {org.editButton}
            </AppButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Info Section */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 2 }}
            color="text.primary"
          >
            {org.detailsSection}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3
            }}
          >
            {renderField("name", companyData.name)}
            {renderField("business-type", companyData.businessType)}
            {renderField("currency-code", companyData.currencyCode)}
            {renderField("registration-number", companyData.registrationNumber)}
            {renderField(
              "vat-percentage",
              formatVatPercentageDisplay(companyData.vatPercentage)
            )}
            {renderField(
              "vat-calculation-mode",
              formatVatCalculationModeDisplay(
                companyData.vatCalculationMode as
                  VatCalculationMode | undefined,
                t
              )
            )}
          </Box>

          {/* Address Section */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mt: 3, mb: 2 }}
            color="text.primary"
          >
            {org.addressSection}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3
            }}
          >
            {getCompanyAddressDisplayFields(companyData.address).map(
              ({ label, value }) => renderField(label, value)
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {renderField("description", companyData.description)}
        </CardContent>
      </Card>

      {/* Edit Modal using the new component */}
      <CompanyEditFormModal open={openEditModal} onClose={handleCloseModal} />
    </>
  );
};

export default CompanyView;
